import React, { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { AIPlan, AIPlanStub } from 'api/interfaces/AIPlan'
import { AIPlanExecutiveSummarySection } from 'api/interfaces/AIPlanExecutiveSummarySection'
import { AIPlanPersonasSection } from 'api/interfaces/AIPlanPersonasSection'
import { useGetDomainsQuery } from 'api/rtkQueryApi/platform/domainsApi'
import { RootState } from 'app/rootReducer'
import { AIPlanMultiTabPdfService, AIPlanMultiTabPdfProgress } from 'services/aiPlanMultiTabPdfService'
import { AIPlanPdfGenerationContext, LogoInput } from 'services/aiPlanTabPdfGenerators'
import { MultiTabPdfProgress } from 'services/multiTabPdfService'

import { startMultiTabPdfGeneration, updateMultiTabPdfProgress, completeMultiTabPdfGeneration, setMultiTabPdfError, clearMultiTabPdfState, selectMultiTabPdfGenerationState, selectMultiTabPdfProgress } from '../features/planner/pages/MasterCampaignPlanPage/pdfViewSlice'

export interface UseAIPlanCombinedPdfGenerationProps {
  aiPlan: AIPlan
  aiPlanStub: AIPlanStub
  executiveSummarySection?: AIPlanExecutiveSummarySection
  personasSection?: AIPlanPersonasSection
  onTabChange: (tabId: string) => void
  tabRefs: {
    ganttChartRef?: React.RefObject<HTMLDivElement>
    marketAnalysisPrintRef?: React.RefObject<HTMLDivElement>
    projectionRefs?: {
      deliveryMetricsRef?: React.RefObject<HTMLDivElement>
      connectedTvRef?: React.RefObject<HTMLDivElement>
      internetDisplayRef?: React.RefObject<HTMLDivElement>
      targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
      billboardLocationsRef?: React.RefObject<HTMLDivElement>
    }
    projectionMapRefs?: {
      targetedHouseholdMapRef?: React.RefObject<any>
      billboardMapRef?: React.RefObject<any>
    }
    mediaplanRef?: React.RefObject<any>
  }
}

export interface UseAIPlanCombinedPdfGenerationResult {
  progress: MultiTabPdfProgress | null
  error: string | null
  generateAllPdfs: (isStaffPdf: boolean, agencyLogo?: LogoInput | null, clientLogo?: LogoInput | null, isDirect?: boolean) => Promise<void>
  clearError: () => void
}

export const useAIPlanCombinedPdfGeneration = ({
  aiPlan,
  aiPlanStub,
  executiveSummarySection,
  personasSection,
  onTabChange,
  tabRefs
}: UseAIPlanCombinedPdfGenerationProps): UseAIPlanCombinedPdfGenerationResult => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount } = useSelector((state: RootState) => state.app)
  const { data: domains } = useGetDomainsQuery()

  const planId = aiPlan.id
  const { isGenerating } = useSelector((state: RootState) => selectMultiTabPdfGenerationState(state, planId))
  const progress = useSelector((state: RootState) => selectMultiTabPdfProgress(state, planId))
  const generationState = useSelector((state: RootState) => state.pdfView.multiTabGeneration[planId])
  const error = generationState?.error || null

  const generateAllPdfs = useCallback(
    async (isStaffPdf: boolean, agencyLogo?: LogoInput | null, clientLogo?: LogoInput | null, isDirect?: boolean) => {
      if (isGenerating || !currentAccount) return

      dispatch(startMultiTabPdfGeneration({ planId, isStaffPdf }))

      const context: AIPlanPdfGenerationContext = {
        aiPlan,
        aiPlanStub,
        organizationName: currentOrganization?.name || '',
        accountName: currentAccount.name || '',
        executiveSummarySection,
        personasSection,
        domains: domains || [],
        agencyLogo: agencyLogo || undefined,
        clientLogo: clientLogo || undefined,
        isDirect: isDirect || false
      }

      const pdfService = new AIPlanMultiTabPdfService(isStaffPdf, (progress: AIPlanMultiTabPdfProgress) => {
        dispatch(updateMultiTabPdfProgress({ planId, progress }))
      })

      try {
        await pdfService.generateAllPdfs(context, onTabChange, tabRefs)
        dispatch(completeMultiTabPdfGeneration(planId))
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        dispatch(setMultiTabPdfError({ planId, error: errorMessage }))
      }
    },
    [isGenerating, currentAccount, aiPlan, aiPlanStub, executiveSummarySection, personasSection, domains, onTabChange, tabRefs, dispatch, planId]
  )

  const clearError = useCallback(() => {
    dispatch(clearMultiTabPdfState(planId))
  }, [dispatch, planId])

  useEffect(() => {
    return () => {
      if (generationState?.isGenerating) {
        dispatch(completeMultiTabPdfGeneration(planId))
      }
    }
  }, [dispatch, planId, generationState?.isGenerating])

  return {
    progress,
    error,
    generateAllPdfs,
    clearError
  }
}
